import type { Entity } from '@api/api/entity-schema';
import { KgInsight } from '@api/api/kg-insights';
import { Button } from '@design/components/button/Button';
import { EXPANDED_VALUE } from '@terminal/components/expandable-drawer/ExpandableDrawer';
import { InsightDrawerAssetHeaderContent } from '@terminal/components/insight-drawer-header/InsightDrawerAssetHeaderContent';
import { InsightDrawerHeaderContent } from '@terminal/components/insight-drawer-header/InsightDrawerHeaderContent';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { CardType } from '@terminal/views/insight-feed/components/insight-feed-card/InsightFeedCard';
import { EarningsInsightEmptyState } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-insight-empty-state/EarningsInsightEmptyState';
import { EarningsInsightTabLines } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/EarningsInsightTabLines';
import { useTranslation } from 'react-i18next';
import { CatalystWidgetView } from '../../CatalystWidgetView';
import { useEarningsKgInsightTab } from '../../hooks/useEarningsKgInsightTab';
import { KgInsightPreview } from '../kg-insight-content/KgInsightPreview';
import { KgInsightSkeleton } from '../kg-insight-content/KgInsightSkeleton';
import { TopTakeaways } from '../top-takeaways/TopTakeaways';
import styles from './EarningsKgInsightWidget.module.scss';

interface EarningsKgInsightWidgetProps {
  navigateToKGInsight: () => void;
  kgInsight: KgInsight;
  entity?: Entity;
}

export const EarningsKgInsightWidget = ({
  navigateToKGInsight,
  kgInsight,
  entity,
}: EarningsKgInsightWidgetProps) => {
  const { t } = useTranslation(['insights']);
  const { params } = useQueryParams();
  const isInsightDrawerHeaderEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-4942-insight-drawer-header')
  );
  const { activeInsight, emptyStateVariant, showLoading } =
    useEarningsKgInsightTab(kgInsight);
  const isExpanded = params[queryKeys.drawerExpanded] === EXPANDED_VALUE;

  if (showLoading) {
    return <KgInsightSkeleton />;
  }

  const insightPreview = activeInsight ? (
    <KgInsightPreview
      kgInsight={activeInsight}
      chartActionTrackingSource={
        isInsightDrawerHeaderEnabled
          ? POSTHOG_TRACKING_SOURCES.EARNINGS_INSIGHT_DRAWER
          : undefined
      }
      hideCatalystBadge={!isInsightDrawerHeaderEnabled && isExpanded}
      hideMetadataHeader={isInsightDrawerHeaderEnabled}
      keyStatsItemsPerRow={3}
    >
      <TopTakeaways
        kgInsight={activeInsight}
        source={POSTHOG_TRACKING_SOURCES.EARNINGS_INSIGHT_DRAWER}
      />
      <Button
        onClick={navigateToKGInsight}
        fullWidth
        label={t('insights:catalystGeneration.readFullCatalyst')}
        data-testid="read-full-catalyst"
      />
    </KgInsightPreview>
  ) : null;

  return (
    <CatalystWidgetView
      headerSlot={
        isInsightDrawerHeaderEnabled && entity ? (
          <InsightDrawerAssetHeaderContent
            entity={entity}
            source={POSTHOG_TRACKING_SOURCES.EARNINGS_INSIGHT_DRAWER}
          />
        ) : undefined
      }
      subject={kgInsight.subject}
      tabsSlot={<EarningsInsightTabLines />}
    >
      {activeInsight ? (
        isInsightDrawerHeaderEnabled ? (
          <div className={styles.content}>
            <InsightDrawerHeaderContent
              cardType={
                activeInsight.category === 'earnings_recap'
                  ? CardType.EarningsRecap
                  : CardType.EarningsPreview
              }
              context={activeInsight.subtitle}
              publishedAt={activeInsight.created_at}
              sentiment={activeInsight.sentiment}
              title={activeInsight.title}
            />
            {insightPreview}
          </div>
        ) : (
          insightPreview
        )
      ) : (
        <EarningsInsightEmptyState variant={emptyStateVariant} />
      )}
    </CatalystWidgetView>
  );
};
